import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Zap, Radar, Crown, CalendarClock, Loader2, X } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { PromoCodeField, promoPrice } from "../PromoCodeField";

const money = (n) => `$${Number(n || 0) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2)}`;
const dayLabel = (iso) => {
  const d = new Date(iso || 0);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const kindLabel = (k) => ({ boost: "Boost", reach: "Boost + Reach", exclusive: "Exclusive" }[k] || "Boost");

// Four things you can actually buy under a radius-pooled model: a slot, a wider radius, the whole
// pool for a week, or the whole pool for the full two weeks. Nothing here promises a fixed
// position, because overlapping pools can't deliver one.
export const BoostOptions = ({ dealId, onClose, onPick, busy, extend = false }) => {
  const { t } = useLang();
  const [slot, setSlot] = useState(null);
  const [promo, setPromo] = useState(null);
  const [quote, setQuote] = useState(null);
  const fitOf = (id) => (quote?.options || []).find((o) => o.packageId === id);

  useEffect(() => {
    api.get("/boost/slots", { params: { deal_id: dealId } }).then((r) => setSlot(r.data)).catch(() => setSlot({}));
    api.get(`/deals/${dealId}/boost-quote`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [dealId]);

  // The sheet owns the scroll while it is open, so the page behind it can never scroll under it.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const opts = [
    { id: "boost_7d", kind: "boost", icon: Zap, price: 10, days: 7, title: t("bo.boostTitle"), body: t("bo.boostBody") },
    { id: "boost_reach_7d", kind: "reach", icon: Radar, price: 18, days: 7, title: t("bo.reachTitle"), body: t("bo.reachBody") },
    { id: "boost_exclusive_7d", kind: "exclusive", icon: Crown, price: 55, days: 7, excl: true, title: t("bo.exclTitle"), body: t("bo.exclBody") },
    { id: "boost_exclusive_14d", kind: "exclusive", icon: CalendarClock, price: 95, days: 14, excl: true, title: t("bo.excl14Title"), body: t("bo.excl14Body") },
  ];
  const exclTaken = slot && slot.used > (slot.mine ? 1 : 0);
  const daysLeft = slot ? (slot.exclusiveDaysLeft ?? 14) : 14;
  // In extend mode, say plainly whether a card just adds days or also upgrades what they're running.
  const changeNote = (o) => {
    const cur = slot?.mineKind;
    if (!extend || !cur) return null;
    if (o.kind === cur) return t("bo.extendSame", { n: o.days });
    if (o.kind === "reach") return t("bo.extendToReach", { n: o.days });
    if (o.kind === "exclusive") return t("bo.extendToExclusive", { n: o.days });
    return t("bo.extendNarrower");
  };

  const sheet = (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
      onClick={onClose} data-testid="boost-options">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        className="bg-card w-full sm:max-w-4xl rounded-t-3xl sm:rounded-3xl border border-border shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[86vh] overflow-hidden">
        <div className="shrink-0 flex items-start gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-border bg-card">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl md:text-lg font-extrabold text-foreground">{extend ? t("bo.extendTitle") : t("bo.title")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {!slot ? t("bo.checking")
                : extend && slot.mineUntil ? t("bo.extendLine", { date: dayLabel(slot.mineUntil) })
                : t("bo.slotsLine", { n: slot.remaining ?? 0, limit: slot.limit ?? 3, km: slot.radiusKm ?? 10 })}
            </p>
          </div>
          <button onClick={onClose} data-testid="boost-options-close" aria-label={t("bo.close")}
            className="shrink-0 h-9 w-9 -mt-1 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5">
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {opts.map((o) => {
              const noRunway = o.excl && daysLeft < o.days;
              // Extending only extends: while a boost is running you can add days to that same
              // boost, not swap it for a different one halfway through.
              const wrongKind = extend && slot?.mineKind && o.kind !== slot.mineKind;
              const blocked = wrongKind || (o.excl && (exclTaken || noRunway));
              return (
                <div key={o.id} className="border border-border rounded-2xl p-4 flex flex-col">
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 shrink-0 rounded-xl bg-secondary flex items-center justify-center"><o.icon size={18} className="text-[#16a34a]" /></span>
                    <div className="min-w-0">
                      <p className="font-bold text-foreground leading-tight">{o.title}</p>
                      {promoPrice(o.price, promo, o.id) != null ? (
                        <p className="text-xl font-extrabold text-[#16a34a]" data-testid={`price-${o.id}`}>
                          {money(promoPrice(o.price, promo, o.id))}
                          <span className="text-sm font-bold text-muted-foreground line-through ml-1.5">{money(o.price)}</span>
                        </p>
                      ) : (
                        <p className="text-xl font-extrabold text-foreground" data-testid={`price-${o.id}`}>
                          {money(fitOf(o.id)?.prorated ? fitOf(o.id).amount : o.price)}
                          {fitOf(o.id)?.prorated && <span className="text-sm font-bold text-muted-foreground line-through ml-1.5">{money(o.price)}</span>}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 flex-1">{o.body}</p>
                  {fitOf(o.id)?.tooShort ? (
                    <p className="text-xs font-semibold text-amber-600 mt-2" data-testid={`fit-note-${o.id}`}>{t("bo.fitTooShort")}</p>
                  ) : fitOf(o.id)?.prorated ? (
                    <p className="text-xs font-semibold text-amber-600 mt-2" data-testid={`fit-note-${o.id}`}>
                      {t("bo.fitShorter", { days: fitOf(o.id).days, full: o.days })}
                    </p>
                  ) : null}
                  {changeNote(o) && !blocked && (
                    <p className={`text-xs font-semibold mt-2 ${o.kind === slot?.mineKind ? "text-muted-foreground" : "text-[#16a34a]"}`} data-testid={`extend-note-${o.id}`}>
                      {changeNote(o)}
                    </p>
                  )}
                  {blocked ? (
                    <p className="text-xs font-semibold text-muted-foreground mt-3" data-testid={`excl-blocked-${o.days}`}>
                      {wrongKind
                        ? t("bo.extendLocked", { kind: kindLabel(slot.mineKind) })
                        : noRunway
                          ? t("bo.exclRunway", { held: slot.exclusiveDaysHeld ?? 0, max: slot.exclusiveMaxDays ?? 14, date: (slot.exclusiveFreeAt || "").slice(0, 10) })
                          : t("bo.exclBlocked", { n: slot.used - (slot.mine ? 1 : 0), date: (slot.poolFreeAt || "").slice(0, 10) })}
                    </p>
                  ) : (
                    <button onClick={() => onPick(o.id, promo?.code)} disabled={!!busy} data-testid={`pick-${o.id}`}
                      className="mt-4 w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white font-bold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
                      {busy === o.id ? <Loader2 className="animate-spin" size={15} /> : <o.icon size={15} />}
                      {extend ? t("bo.addDays", { n: o.days }) : t("bo.choose")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-border bg-card space-y-2">
          <PromoCodeField promo={promo} onApply={setPromo} compact />
          <p className="text-xs text-muted-foreground">{extend ? t("bo.extendNote") : t("bo.note")}</p>
        </div>
      </div>
    </div>
  );

  // Portalled to <body>: several page wrappers keep a transform from their entrance animation,
  // which would otherwise make this "fixed" sheet land at the bottom of the page instead of on top of it.
  return createPortal(sheet, document.body);
};
